/* eslint-disable no-console */
import _ from 'lodash';
import * as React from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  AnalyticsHeaderRow,
  ConsumptionBox,
  getSnapInIcon,
  DevicePowerConsumption,
  hideLoader,
  LoaderMode,
  ModalLoader,
  showLoader
} from '../../..';
import { Text } from '../../../components/Text';
import { devices, getDeviceIcon } from '../../../util';
import {
  DeviceLevelPowerConsumptionDto,
  RoomLevelPowerConsumptionDto
} from '../../../api';

const customScrollBar = css`
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) =>
      theme.isDark ? 'rgba(255, 255, 255, 0.05)' : '#F7F7F8'};
    border-radius: 20px;
  }

  &::-webkit-scrollbar {
    width: 9px;
    height: 9px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => (!theme.isDark ? '#65686B' : '#F7F7F8')};
    border-radius: 20px;
  }
`;

const RoomAnalyticsOverlay = styled.div<{ modalOpen?: boolean }>`
  position: absolute;
  width: 100%;
  left: 0px;
  top: 0px;
  height: 100vh;
  background: black;
  opacity: ${({ modalOpen }) => (modalOpen ? '0.5' : '0')};
  transition: all 0.5s cubic-bezier(0.4, 0, 1, 1);
  z-index: ${({ modalOpen }) => (modalOpen ? '98' : '-1')};
  cursor: pointer;
`;

const RoomAnalyticsWrapper = styled.div<{ modalOpen: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: auto;
  left: 0px;
  right: 0px;
  top: ${({ modalOpen }) => (modalOpen ? '110px' : '100%')};
  height: calc(100% - 110px);
  background: ${({ theme }) =>
    theme.isDark ? '#3b3c43' : 'rgba(255, 255, 255)'};
  border-radius: 24px 24px 0px 0px;
  z-index: 99;
  transition: all 0.5s cubic-bezier(0.4, 0, 1, 1);
  padding: 46px 40px;
  ${({ theme }) => theme.mediaQueries.medium} {
    padding: 46px 90px;
  }
  overflow: hidden;
}
`;

export const ConsuptionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
  height: 133px;
  margin-top: 48px;
`;

export const StyledConsumptionSpacer = styled.div`
  height: 1px;
  background: ${({ theme }) => (theme.isDark ? '#45464C' : '#F7F7F8')};
  opacity: 0.5;
  width: 100%;
  margin: 32px 0px;
`;

const StyledSnapInTable = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 2px;
  overflow-y: auto;
  overflow-x: hidden;
  ${customScrollBar};
`;

export const SnapInTableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-gap: 16px;
`;

const DeviceConsumptionTableRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-right: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  ${customScrollBar};
`;

export const SnapInTableRowWrapper = styled.div`
  width: 100%;
  padding: 18px 0px;
  border: 1px solid
    ${({ theme }) => (theme.isDark ? 'rgba(255, 255, 255, 0.05)' : '#F7F7F8')};
  border-radius: 10px;
  margin-bottom: 10px;
`;

const SnapInView = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 42px;
  ${({ theme }) => theme.mediaQueries.medium} {
    padding-left: 72px;
  }
`;

const RoomAnalyticsText = styled(Text)`
  color: ${({ theme }) => theme.colors.font3};
`;

export const loadData = (fetchingData: boolean) => {
  if (fetchingData) {
    return showLoader(<ModalLoader />, {
      mode: LoaderMode.COVER,
      loaderContainerSelector: 'location_analytics_table'
    });
  }
  return hideLoader();
};
export interface IRoomAnalyticsModalProps {
  roomModalOpen: boolean;
  roomModalHandler: (open: boolean) => void;
  roomData: RoomLevelPowerConsumptionDto;
  fetchingData?: boolean;
}

const RoomAnalyticsModal: React.FunctionComponent<IRoomAnalyticsModalProps> = props => {
  const { roomModalOpen, roomModalHandler, roomData, fetchingData } = props;

  React.useEffect(() => {
    loadData(fetchingData);
  }, [fetchingData]);

  function groupBy(
    devicesArray: DeviceLevelPowerConsumptionDto[],
    property: string
  ) {
    return devicesArray.reduce((prevZigbee, currZigbee) => {
      if (!prevZigbee[currZigbee[property]]) {
        prevZigbee[currZigbee[property]] = [];
      }
      prevZigbee[currZigbee[property]].push(currZigbee);
      return prevZigbee;
    }, {});
  }

  const roomZigbees = roomData ? groupBy(roomData.devices, 'zigbeeId') : [];
  const roomZigbeeIds = roomData ? Object.keys(roomZigbees) : [];

  return (
    <>
      <RoomAnalyticsOverlay
        onClick={() => roomModalHandler(false)}
        modalOpen={roomModalOpen}
      />
      <RoomAnalyticsWrapper
        modalOpen={roomModalOpen}
        className="room_analytics_table"
      >
        {roomData && (
          <>
            <AnalyticsHeaderRow
              headerTitle={roomData.roomName}
              roomTypeId={roomData.typeId}
              closeModalHandler={() => roomModalHandler(false)}
              lastUpdatedTime={moment()}
            />
            <ConsuptionsRow>
              <ConsumptionBox
                currentConsumption={roomData.roomPowerConsumption.power}
                description="Power Consumption"
              />
              <ConsumptionBox
                currentConsumption={roomData.smartPowerConsumption.power}
                description="Sockets Consumption"
              />
              <ConsumptionBox
                currentConsumption={roomData.relayPowerConsumption.power}
                description="Relays Consumption"
              />
            </ConsuptionsRow>
            <StyledConsumptionSpacer />
            <StyledSnapInTable>
              {roomData &&
                roomZigbeeIds.map(zigbeeId => {
                  const singleZigbee: DeviceLevelPowerConsumptionDto[] =
                    roomZigbees[zigbeeId];
                  const DeviceIcon = getDeviceIcon(
                    singleZigbee[0].deviceSymbol
                  );
                  const SnapInImage = getSnapInIcon(singleZigbee[0].snapInCode);
                  return (
                    <SnapInTableRowWrapper key={singleZigbee[0].deviceNumber}>
                      <SnapInTableRow>
                        <SnapInView>
                          <SnapInImage key={zigbeeId} width={176} height={64} />
                          <RoomAnalyticsText
                            bold
                            lineHeight="24px"
                            fontSize="12px"
                            mt="9px"
                          >
                            Near main doors
                          </RoomAnalyticsText>
                        </SnapInView>
                        <DeviceConsumptionTableRow>
                          {singleZigbee.map(device => {
                            return (
                              <DevicePowerConsumption
                                key={device.deviceNumber}
                                deviceName={
                                  Object.values(devices).find(
                                    dev => dev.symbol === device.deviceSymbol
                                  ).name || ''
                                }
                                deviceNumber={device.deviceNumber}
                                deviceIcon={<DeviceIcon />}
                                currentConsumption={
                                  device.powerState.powerConsumption?.power || 0
                                }
                                maxConsumption={
                                  roomData.roomPowerConsumption?.power || 0
                                }
                              />
                            );
                          })}
                        </DeviceConsumptionTableRow>
                      </SnapInTableRow>
                    </SnapInTableRowWrapper>
                  );
                })}
            </StyledSnapInTable>
          </>
        )}
      </RoomAnalyticsWrapper>
    </>
  );
};

export default RoomAnalyticsModal;
